import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, Shield, RefreshCw, ExternalLink } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import {
  getDkimMigrationStatus,
  DkimMigrationClient,
  DkimMigrationSummary,
  DkimMigrationStatusValue,
} from '../../services/api';

type FilterStatus = 'all' | DkimMigrationStatusValue;

const STATUS_META: Record<DkimMigrationStatusValue, { label: string; tone: string; icon: typeof CheckCircle; description: string }> = {
  current: {
    label: 'Current',
    tone: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    icon: CheckCircle,
    description: 'DNS records use the new Microsoft DKIM format. No action needed.',
  },
  migrated: {
    label: 'Broken',
    tone: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
    icon: AlertTriangle,
    description: 'Microsoft has migrated this tenant but DNS still points to the legacy target. DKIM signing is broken — update DNS now.',
  },
  'not-migrated': {
    label: 'At Risk',
    tone: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    icon: Clock,
    description: 'DNS uses the legacy .onmicrosoft.com format. Currently works — will break when Microsoft migrates the tenant.',
  },
  'no-config': {
    label: 'No Config',
    tone: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    icon: Shield,
    description: 'No DKIM signing configuration exists in Exchange Online.',
  },
  'no-dkim': {
    label: 'No DKIM',
    tone: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    icon: Shield,
    description: 'No DKIM CNAMEs in DNS.',
  },
  unknown: {
    label: 'Unknown',
    tone: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
    icon: Shield,
    description: 'Status could not be determined on the last check.',
  },
};

function StatusBadge({ status }: { status: DkimMigrationStatusValue }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${meta.tone}`}>
      <Icon className="w-3.5 h-3.5" />
      {meta.label}
    </span>
  );
}

export default function DkimMigrationPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DkimMigrationSummary | null>(null);
  const [clients, setClients] = useState<DkimMigrationClient[]>([]);
  const [filter, setFilter] = useState<FilterStatus>('all');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getDkimMigrationStatus();
      setSummary(res.summary);
      setClients(res.clients);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load DKIM migration status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return clients;
    return clients.filter(c => c.migrationStatus === filter);
  }, [clients, filter]);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">DKIM Migration Status</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Microsoft is migrating Exchange Online DKIM hosting from <code>*.onmicrosoft.com</code> to{' '}
            <code>*-v1.dkim.mail.microsoft</code>. When a tenant gets migrated, DNS CNAMEs at the customer's
            registrar must be updated or outbound DKIM signing breaks immediately.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border bg-card hover:bg-accent disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <SummaryCard
            label="Total"
            count={summary.total}
            active={filter === 'all'}
            onClick={() => setFilter('all')}
            tone="bg-card"
          />
          <SummaryCard
            label="Broken"
            count={summary.migrated}
            active={filter === 'migrated'}
            onClick={() => setFilter('migrated')}
            tone="bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900"
          />
          <SummaryCard
            label="At Risk"
            count={summary.notMigrated}
            active={filter === 'not-migrated'}
            onClick={() => setFilter('not-migrated')}
            tone="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900"
          />
          <SummaryCard
            label="Current"
            count={summary.current}
            active={filter === 'current'}
            onClick={() => setFilter('current')}
            tone="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-900"
          />
          <SummaryCard
            label="No Config"
            count={summary.noConfig}
            active={filter === 'no-config'}
            onClick={() => setFilter('no-config')}
            tone="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
          />
          <SummaryCard
            label="No DKIM"
            count={summary.noDkim}
            active={filter === 'no-dkim'}
            onClick={() => setFilter('no-dkim')}
            tone="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
          />
          <SummaryCard
            label="Unknown"
            count={summary.unknown}
            active={filter === 'unknown'}
            onClick={() => setFilter('unknown')}
            tone="bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700"
          />
        </div>
      )}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium">Client</th>
                <th className="px-4 py-2.5 font-medium">Domain</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">DNS Target</th>
                <th className="px-4 py-2.5 font-medium">Exchange Expects</th>
                <th className="px-4 py-2.5 font-medium">Signing</th>
                <th className="px-4 py-2.5 font-medium">Last Check</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Loading…</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No clients match this filter.</td>
                </tr>
              )}
              {!loading && filtered.map(c => (
                <tr key={c.clientId} className="border-b border-border hover:bg-accent/30">
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/clients/${c.clientId}`}
                      className="text-foreground hover:text-blue-600 dark:hover:text-blue-400 inline-flex items-center gap-1"
                    >
                      {c.clientName}
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </Link>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{c.domain}</td>
                  <td className="px-4 py-2.5"><StatusBadge status={c.migrationStatus} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground max-w-md truncate" title={c.selector1Actual || ''}>
                    {c.selector1Actual || <span className="italic">none</span>}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground max-w-md truncate" title={c.selector1Expected || ''}>
                    {c.selector1Expected || <span className="italic">none</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    {c.dkimSigningEnabled === true && <span className="text-green-600 dark:text-green-400">on</span>}
                    {c.dkimSigningEnabled === false && <span className="text-amber-600 dark:text-amber-400">off</span>}
                    {c.dkimSigningEnabled === undefined && <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">
                    {c.checkedAt ? formatDistanceToNow(parseISO(c.checkedAt), { addSuffix: true }) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-muted-foreground space-y-1">
        <p><strong>Broken</strong> — Microsoft has flipped this tenant. Update DNS CNAMEs to <code>*-v1.dkim.mail.microsoft</code> now.</p>
        <p><strong>At Risk</strong> — Tenant still serves keys via legacy format; signing works today. Microsoft will migrate sometime — we'll alert immediately when they do.</p>
        <p><strong>Current</strong> — DNS already uses the new format. No action.</p>
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  count,
  active,
  onClick,
  tone,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-lg border p-3 text-left transition ${tone} ${active ? 'ring-2 ring-blue-500' : 'hover:ring-1 hover:ring-blue-300'}`}
    >
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-0.5">{count}</div>
    </button>
  );
}
